/**
 * useVapiAudio Hook
 * 
 * Handles real-time voice conversations with VAPI (Voice API)
 * This replaces the OpenAI Realtime API implementation with a more cost-effective solution
 * 
 * Cost: ~$0.14/min (vs ~$0.35/min for OpenAI Realtime)
 * Latency: ~300-600ms (vs ~600-1200ms for OpenAI Realtime)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { apiRequest } from "@/lib/queryClient";
import Vapi from '@vapi-ai/web';

export type VapiStatus =
    | 'disconnected'
    | 'connecting'
    | 'connected'
    | 'listening'
    | 'speaking'
    | 'processing';

export interface VapiAudioConfig {
    publicKey: string;
    assistantId: string;
    restaurantId: string;
    tableId?: string;
    tableNumber?: string;
    customerSessionId?: string;
    restaurantName?: string;
    restaurantState?: string;
    restaurantDescription?: string;
    restaurantHours?: string;
    restaurantPhone?: string;
    restaurantAddress?: string;
    menuItems?: any[];
    restaurantKnowledge?: {
        story?: string;
        philosophy?: string;
        sourcingPractices?: string;
        specialTechniques?: string;
        awards?: string;
        sustainabilityPractices?: string;
    } | null;
    faqKnowledge?: Array<{ question: string; answer: string }>;
    currentCart?: Array<{
        id: string;
        name: string;
        price: string;
        quantity: number;
        customerNote?: string;
        allergies?: string;
    }>;
    language?: string;
    onAddToCart?: (item: any) => void;
    onRemoveFromCart?: (item: any) => void;
    onOrderConfirmed?: (order: any) => void;
    onChefCalled?: (message: string) => void;
    onWaiterCalled?: (message: string) => void;
    onCheckout?: (tipAmount?: number, customerNote?: string) => void;
    onTranscript?: (text: string, role: 'user' | 'assistant') => void;
    onError?: (error: string) => void;
    onStatusChange?: (status: VapiStatus) => void;
}

export function useVapiAudio(config: VapiAudioConfig) {
    const [status, setStatus] = useState<VapiStatus>('disconnected');
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [reinitTrigger, setReinitTrigger] = useState(0);

    const vapiRef = useRef<Vapi | null>(null);
    const statusRef = useRef<VapiStatus>('disconnected');
    const configRef = useRef(config);

    // Prevent re-initialization during disconnect
    const isInitializedRef = useRef(false);

    // Track if user manually disconnected - prevents events from re-activating
    const manuallyDisconnectedRef = useRef(false);

    // Inactivity timeout tracking
    const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const INACTIVITY_TIMEOUT_MS = 20000; // 20 seconds
    const lastActivityTimeRef = useRef<number>(Date.now());

    // AI Usage tracking
    const usageTimerRef = useRef<any>(null);
    const lastReportTimeRef = useRef<number>(0);

    // Track processed function call IDs to prevent duplicate processing
    const processedFunctionCallsRef = useRef<Set<string>>(new Set());

    // Promise resolver for initialization completion
    const initPromiseRef = useRef<{ resolve: () => void; reject: (err: Error) => void } | null>(null);

    // Keep config ref updated
    useEffect(() => {
        configRef.current = config;
    }, [config]);

    // Update status helper
    const updateStatus = useCallback((newStatus: VapiStatus) => {
        setStatus(newStatus);
        statusRef.current = newStatus;
        config.onStatusChange?.(newStatus);
    }, [config]);

    // Reset inactivity timer
    const resetActivityTimer = useCallback(() => {
        lastActivityTimeRef.current = Date.now();

        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }

        if (statusRef.current !== 'disconnected' && statusRef.current !== 'connecting') {
            inactivityTimeoutRef.current = setTimeout(() => {
                const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;
                if (timeSinceLastActivity >= INACTIVITY_TIMEOUT_MS) {
                    console.log('[VapiAudio] Auto-disconnecting due to 30s inactivity');
                    configRef.current.onError?.('Session ended due to inactivity');
                    vapiRef.current?.stop();
                    updateStatus('disconnected');
                    setIsSessionActive(false);
                }
            }, INACTIVITY_TIMEOUT_MS);
        }
    }, [updateStatus]);

    // Clear inactivity timer
    const clearActivityTimer = useCallback(() => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        }
    }, []);

    // Handle function calls from VAPI
    const handleFunctionCall = useCallback(async (
        functionName: string,
        args: any
    ): Promise<any> => {
        console.log('[VapiAudio] Function call:', functionName, args);
        resetActivityTimer();

        const currentConfig = configRef.current;

        switch (functionName) {
            case 'add_to_cart': {
                const items = args.items || [args];
                const menuItems = currentConfig.menuItems || [];
                const addedItems: string[] = [];

                for (const itemRequest of items) {
                    const menuItem = menuItems.find(
                        (m: any) => m.name.toLowerCase() === itemRequest.item_name?.toLowerCase()
                    );

                    if (menuItem) {
                        const quantity = itemRequest.quantity || 1;
                        currentConfig.onAddToCart?.({
                            ...menuItem,
                            quantity,
                            customerNote: itemRequest.special_instructions,
                            allergies: itemRequest.allergies,
                        });
                        addedItems.push(`${quantity}x ${menuItem.name}`);
                    }
                }

                return {
                    success: addedItems.length > 0,
                    message: addedItems.length > 0
                        ? `Added: ${addedItems.join(', ')}`
                        : 'Could not find those items on the menu',
                };
            }

            case 'remove_from_cart': {
                const items = args.items || [args];
                const removedItems: string[] = [];

                for (const itemRequest of items) {
                    currentConfig.onRemoveFromCart?.({
                        name: itemRequest.item_name,
                        quantity: itemRequest.quantity || 1,
                    });
                    removedItems.push(itemRequest.item_name);
                }

                return {
                    success: removedItems.length > 0,
                    message: `Removed: ${removedItems.join(', ')}`,
                };
            }

            case 'confirm_order': {
                // Build cart items for the webhook
                const cartItems = currentConfig.currentCart?.map((item) => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    customerNote: item.customerNote,
                    allergies: item.allergies,
                })) || [];

                try {
                    // Use the backend API URL from environment
                    const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
                    const response = await fetch(`${apiBaseUrl}/api/vapi/webhook`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'function-call',
                            functionCall: {
                                name: 'confirm_order',
                                parameters: {
                                    ...args,
                                    cart_items: cartItems,
                                },
                            },
                            call: {
                                id: 'web-session',
                                assistantOverrides: {
                                    variableValues: {
                                        restaurantId: currentConfig.restaurantId,
                                        tableId: currentConfig.tableId,
                                        tableNumber: currentConfig.tableNumber,
                                        sessionId: currentConfig.customerSessionId,
                                        restaurantState: currentConfig.restaurantState,
                                    },
                                },
                            },
                        }),
                    });

                    console.log('[VapiAudio] confirm_order API response status:', response.status);

                    if (!response.ok) {
                        console.error('[VapiAudio] confirm_order API failed:', response.status, response.statusText);
                        return { success: false, message: 'API call failed' };
                    }

                    const result = await response.json();
                    console.log('[VapiAudio] confirm_order API result:', JSON.stringify(result));

                    if (result.result?.success) {
                        console.log('[VapiAudio] Order successful, calling onOrderConfirmed with:', result.result);
                        currentConfig.onOrderConfirmed?.(result.result);
                    } else {
                        console.warn('[VapiAudio] Order not successful:', result.result?.message || result);
                    }

                    return result.result;
                } catch (error) {
                    console.error('[VapiAudio] Error confirming order:', error);
                    return {
                        success: false,
                        message: 'Failed to place order. Please try again.',
                    };
                }
            }

            case 'call_chef': {
                currentConfig.onChefCalled?.(args.message);

                try {
                    const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
                    const response = await fetch(`${apiBaseUrl}/api/vapi/webhook`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'function-call',
                            functionCall: { name: 'call_chef', parameters: args },
                            call: {
                                id: 'web-session',
                                assistantOverrides: {
                                    variableValues: {
                                        restaurantId: currentConfig.restaurantId,
                                        tableNumber: currentConfig.tableNumber,
                                        sessionId: currentConfig.customerSessionId,
                                    },
                                },
                            },
                        }),
                    });

                    const result = await response.json();
                    return result.result;
                } catch (error) {
                    console.error('[VapiAudio] Error calling chef:', error);
                    return { success: false, message: 'Failed to contact chef.' };
                }
            }

            case 'call_waiter': {
                currentConfig.onWaiterCalled?.(args.message);

                try {
                    const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
                    const response = await fetch(`${apiBaseUrl}/api/vapi/webhook`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type: 'function-call',
                            functionCall: { name: 'call_waiter', parameters: args },
                            call: {
                                id: 'web-session',
                                assistantOverrides: {
                                    variableValues: {
                                        restaurantId: currentConfig.restaurantId,
                                        tableId: currentConfig.tableId,
                                        sessionId: currentConfig.customerSessionId,
                                    },
                                },
                            },
                        }),
                    });

                    const result = await response.json();
                    return result.result;
                } catch (error) {
                    console.error('[VapiAudio] Error calling waiter:', error);
                    return { success: false, message: 'Failed to call waiter.' };
                }
            }

            case 'open_checkout': {
                console.log('[VapiAudio] open_checkout called with tip:', args.tip_amount, 'note:', args.customer_note);

                // Call the checkout callback
                currentConfig.onCheckout?.(args.tip_amount, args.customer_note);
                console.log('[VapiAudio] onCheckout callback triggered successfully');

                // Return a very explicit success message so the AI knows it worked
                return {
                    success: true,
                    status: 'completed',
                    message: 'SUCCESS: The checkout page has been opened on the customer\'s screen. They can now see the payment form and complete their order. Tell them you\'ve opened the checkout page and wish them a great meal.',
                };
            }

            default:
                return { success: false, message: `Unknown function: ${functionName}` };
        }
    }, [resetActivityTimer]);

    // Initialize VAPI client - only once per mount
    useEffect(() => {
        if (!config.publicKey) {
            console.warn('[VapiAudio] No public key provided');
            return;
        }

        // CRITICAL: Skip initialization if user has manually disconnected
        // This prevents a new VAPI instance from being created during disconnect
        if (manuallyDisconnectedRef.current) {
            console.log('[VapiAudio] Skipping init (manuallyDisconnected=true)');
            return;
        }

        // Skip if already initialized with a valid instance
        if (isInitializedRef.current && vapiRef.current) {
            console.log('[VapiAudio] Already initialized, skipping');
            return;
        }

        try {
            isInitializedRef.current = true;
            vapiRef.current = new Vapi(config.publicKey);

            // Resolve the init promise if someone is waiting
            if (initPromiseRef.current) {
                initPromiseRef.current.resolve();
                initPromiseRef.current = null;
            }

            // Event: Call started
            vapiRef.current.on('call-start', () => {
                // Ignore if we've manually disconnected
                if (manuallyDisconnectedRef.current) {
                    console.log('[VapiAudio] Ignoring call-start (manuallyDisconnected=true)');
                    return;
                }
                if (statusRef.current === 'disconnected') {
                    console.log('[VapiAudio] Ignoring call-start (status=disconnected)');
                    return;
                }
                console.log('[VapiAudio] Call started');
                updateStatus('connected');
                setIsSessionActive(true);
                resetActivityTimer();
            });

            // Event: Call ended
            vapiRef.current.on('call-end', () => {
                console.log('[VapiAudio] Call ended');
                clearActivityTimer();
                updateStatus('disconnected');
                setIsSessionActive(false);
            });

            // Event: Speech started (AI is speaking)
            vapiRef.current.on('speech-start', () => {
                // Only update status if not disconnected
                if (manuallyDisconnectedRef.current || statusRef.current === 'disconnected') {
                    console.log('[VapiAudio] Ignoring speech-start (disconnected)');
                    return;
                }
                console.log('[VapiAudio] AI speaking');
                updateStatus('speaking');
                resetActivityTimer();
            });

            // Event: Speech ended (AI finished speaking)
            vapiRef.current.on('speech-end', () => {
                // Only update status if not disconnected
                if (manuallyDisconnectedRef.current || statusRef.current === 'disconnected') {
                    console.log('[VapiAudio] Ignoring speech-end (disconnected)');
                    return;
                }
                console.log('[VapiAudio] AI finished speaking');
                updateStatus('listening');
                resetActivityTimer();
            });

            // Event: Message (transcripts, function calls, etc.)
            vapiRef.current.on('message', (message: any) => {
                // Ignore all messages if disconnected
                if (manuallyDisconnectedRef.current || statusRef.current === 'disconnected') {
                    console.log('[VapiAudio] Ignoring message (disconnected):', message.type);
                    return;
                }

                console.log('[VapiAudio] Message received:', message.type, message);
                resetActivityTimer();

                if (message.type === 'transcript') {
                    configRef.current.onTranscript?.(
                        message.transcript,
                        message.role as 'user' | 'assistant'
                    );
                }

                // Handle function-call message type
                if (message.type === 'function-call' && message.functionCall) {
                    console.log('[VapiAudio] Processing function-call:', message.functionCall.name);
                    handleFunctionCall(
                        message.functionCall.name,
                        message.functionCall.parameters
                    ).then((result) => {
                        console.log('[VapiAudio] Function result:', result);
                        // Send function result back to VAPI
                        vapiRef.current?.send({
                            type: 'add-message',
                            message: {
                                role: 'tool',
                                toolCallId: message.functionCall.id,
                                content: JSON.stringify(result),
                            },
                        });
                    });
                }

                // Handle tool-calls message type (alternative format)
                if (message.type === 'tool-calls' && message.toolCallList) {
                    console.log('[VapiAudio] Processing tool-calls:', message.toolCallList);
                    for (const toolCall of message.toolCallList) {
                        handleFunctionCall(
                            toolCall.function?.name || toolCall.name,
                            toolCall.function?.arguments ? JSON.parse(toolCall.function.arguments) : toolCall.parameters
                        ).then((result) => {
                            console.log('[VapiAudio] Tool result:', result);
                            // For client-side tools, we don't always need to send back
                            // But try anyway for server-side tools
                            try {
                                vapiRef.current?.send({
                                    type: 'add-message',
                                    message: {
                                        role: 'tool',
                                        toolCallId: toolCall.id,
                                        content: JSON.stringify(result),
                                    },
                                });
                            } catch (e) {
                                console.log('[VapiAudio] Tool result send skipped (client-side tool)');
                            }
                        });
                    }
                }

                // Handle conversation-update - extract function calls from messages array
                // This is needed when serverUrl is set and functions are processed server-side
                if (message.type === 'conversation-update') {
                    // Try messages array first (VAPI format)
                    const messagesToCheck = message.messages || message.conversation || [];

                    for (const msg of messagesToCheck) {
                        // VAPI uses role='tool_calls' with toolCalls array (camelCase)
                        // NOT role='assistant' with tool_calls (snake_case)
                        if (msg.role === 'tool_calls' && msg.toolCalls) {
                            console.log('[VapiAudio] Found tool_calls message:', msg.toolCalls);

                            for (const toolCall of msg.toolCalls) {
                                // Get the call ID - could be toolCall.id or generate from name+time
                                const callId = toolCall.id || `${toolCall.function?.name || toolCall.name}_${msg.time}`;

                                // Use the ref to track processed calls across multiple updates
                                if (callId && !processedFunctionCallsRef.current.has(callId)) {
                                    processedFunctionCallsRef.current.add(callId);

                                    // Get function name - could be in function.name or directly on toolCall
                                    const functionName = toolCall.function?.name || toolCall.name;

                                    // Get arguments - could be in function.arguments or parameters
                                    let functionArgs: any = {};
                                    try {
                                        if (toolCall.function?.arguments) {
                                            functionArgs = typeof toolCall.function.arguments === 'string'
                                                ? JSON.parse(toolCall.function.arguments)
                                                : toolCall.function.arguments;
                                        } else if (toolCall.parameters) {
                                            functionArgs = toolCall.parameters;
                                        }
                                    } catch (e) {
                                        console.warn('[VapiAudio] Failed to parse function args:', e);
                                    }

                                    console.log('[VapiAudio] Processing function:', functionName, functionArgs);

                                    // Process functions that need client-side handling
                                    // These need to update the UI and/or make API calls with cart data
                                    if (functionName === 'add_to_cart' || functionName === 'remove_from_cart') {
                                        handleFunctionCall(functionName, functionArgs).then((result) => {
                                            console.log('[VapiAudio] Client-side function processed:', result);
                                            // Send result back to VAPI so it knows what happened
                                            try {
                                                vapiRef.current?.send({
                                                    type: 'add-message',
                                                    message: {
                                                        role: 'tool',
                                                        toolCallId: callId,
                                                        content: JSON.stringify(result),
                                                    },
                                                });
                                            } catch (e) {
                                                console.warn('[VapiAudio] Could not send cart result to VAPI:', e);
                                            }
                                        });
                                    }
                                    // For confirm_order - we need to call handleFunctionCall because:
                                    // 1. The server webhook doesn't have access to cart_items
                                    // 2. We need to send cart data from the frontend
                                    else if (functionName === 'confirm_order') {
                                        console.log('[VapiAudio] Processing confirm_order client-side with cart data');
                                        handleFunctionCall(functionName, functionArgs).then((result) => {
                                            console.log('[VapiAudio] Order confirmation result:', result);
                                            // Send result back to VAPI
                                            try {
                                                vapiRef.current?.send({
                                                    type: 'add-message',
                                                    message: {
                                                        role: 'tool',
                                                        toolCallId: callId,
                                                        content: JSON.stringify(result),
                                                    },
                                                });
                                            } catch (e) {
                                                console.warn('[VapiAudio] Could not send order result to VAPI:', e);
                                            }
                                            if (result?.success) {
                                                configRef.current.onOrderConfirmed?.(result);
                                            }
                                        }).catch((error) => {
                                            console.error('[VapiAudio] Error confirming order:', error);
                                        });
                                    }
                                    // For call_chef - call handleFunctionCall to make API request
                                    else if (functionName === 'call_chef') {
                                        console.log('[VapiAudio] Processing call_chef - making API request');
                                        handleFunctionCall(functionName, functionArgs).then((result) => {
                                            console.log('[VapiAudio] Call chef result:', result);
                                            // Send result back to VAPI
                                            try {
                                                vapiRef.current?.send({
                                                    type: 'add-message',
                                                    message: {
                                                        role: 'tool',
                                                        toolCallId: callId,
                                                        content: JSON.stringify(result),
                                                    },
                                                });
                                            } catch (e) {
                                                console.warn('[VapiAudio] Could not send chef result to VAPI:', e);
                                            }
                                            if (result?.success) {
                                                configRef.current.onChefCalled?.(functionArgs.message || 'Question sent to chef');
                                            }
                                        }).catch((error) => {
                                            console.error('[VapiAudio] Error calling chef:', error);
                                        });
                                    }
                                    // For call_waiter - call handleFunctionCall to make API request
                                    else if (functionName === 'call_waiter') {
                                        console.log('[VapiAudio] Processing call_waiter - making API request');
                                        handleFunctionCall(functionName, functionArgs).then((result) => {
                                            console.log('[VapiAudio] Call waiter result:', result);
                                            // Send result back to VAPI
                                            try {
                                                vapiRef.current?.send({
                                                    type: 'add-message',
                                                    message: {
                                                        role: 'tool',
                                                        toolCallId: callId,
                                                        content: JSON.stringify(result),
                                                    },
                                                });
                                            } catch (e) {
                                                console.warn('[VapiAudio] Could not send waiter result to VAPI:', e);
                                            }
                                            if (result?.success) {
                                                configRef.current.onWaiterCalled?.(functionArgs.message || 'Waiter called');
                                            }
                                        }).catch((error) => {
                                            console.error('[VapiAudio] Error calling waiter:', error);
                                        });
                                    }
                                    else if (functionName === 'open_checkout') {
                                        console.log('[VapiAudio] Processing open_checkout client-side');
                                        handleFunctionCall(functionName, functionArgs).then((result) => {
                                            console.log('[VapiAudio] open_checkout result:', result);
                                            // Send result back to VAPI
                                            try {
                                                vapiRef.current?.send({
                                                    type: 'add-message',
                                                    message: {
                                                        role: 'tool',
                                                        toolCallId: callId,
                                                        content: JSON.stringify(result),
                                                    },
                                                });
                                                console.log('[VapiAudio] Sent open_checkout result to VAPI');
                                            } catch (e) {
                                                console.warn('[VapiAudio] Could not send checkout result to VAPI:', e);
                                            }
                                        }).catch((error) => {
                                            console.error('[VapiAudio] Error in open_checkout:', error);
                                        });
                                    }
                                }
                            }
                        }
                    }
                }
            });

            // Event: Volume level (for visualizations)
            vapiRef.current.on('volume-level', (_volume: number) => {
                // Can be used for audio visualizations
                // Volume is between 0 and 1
            });

            // Event: Error
            vapiRef.current.on('error', (error: any) => {
                console.error('[VapiAudio] ========== VAPI ERROR ==========');
                console.error('[VapiAudio] Error type:', error?.type);
                console.error('[VapiAudio] Error stage:', error?.stage);

                // The actual error message is in error.error
                const nestedError = error?.error;
                if (nestedError) {
                    console.error('[VapiAudio] Nested error:', nestedError);
                    console.error('[VapiAudio] Nested error message:', nestedError?.message);
                    console.error('[VapiAudio] Nested error statusCode:', nestedError?.statusCode);
                    console.error('[VapiAudio] Nested error body:', nestedError?.body);
                }

                // Try to get actual error message
                const errorMsg = error?.message ||
                    error?.errorMessage ||
                    nestedError?.message ||
                    nestedError?.body?.message ||
                    'Unknown VAPI error';

                console.error('[VapiAudio] Extracted error message:', errorMsg);
                console.error('[VapiAudio] Full error object:', JSON.stringify(error, null, 2));
                console.error('[VapiAudio] ========== END ERROR ==========');

                configRef.current.onError?.(errorMsg);
            });

            console.log('[VapiAudio] VAPI client initialized');
        } catch (error) {
            console.error('[VapiAudio] Failed to initialize VAPI:', error);
            config.onError?.('Failed to initialize voice service');
        }

        return () => {
            isInitializedRef.current = false;
            vapiRef.current?.stop();
            clearActivityTimer();
        };
    }, [config.publicKey, reinitTrigger, updateStatus, resetActivityTimer, clearActivityTimer, handleFunctionCall]);

    // Build menu list for system prompt - with size limit to prevent payload issues
    const buildMenuList = useCallback(() => {
        const menuItems = config.menuItems || [];
        const menuList = menuItems
            .filter((item: any) => item.available !== false)
            .map((item: any) => {
                // Safely handle item properties
                const name = String(item.name || 'Unknown').replace(/[^\w\s$.,!?-]/g, '');
                const price = String(item.price || '0');
                const desc = String(item.description || 'No description').substring(0, 100);

                let details = `- ${name} ($${price}): ${desc}`;
                const tags = [];
                if (item.isVegan) tags.push('Vegan');
                if (item.isVegetarian) tags.push('Vegetarian');
                if (item.isGlutenFree) tags.push('Gluten-Free');
                if (item.isHalal) tags.push('Halal');
                if (item.isKosher) tags.push('Kosher');
                if (tags.length > 0) details += ` [${tags.join(', ')}]`;
                return details;
            })
            .join('\n');

        // Limit total size to prevent VAPI payload issues (max ~8KB for variables)
        const MAX_SIZE = 8000;
        if (menuList.length > MAX_SIZE) {
            console.warn(`[VapiAudio] Menu list truncated from ${menuList.length} to ${MAX_SIZE} chars`);
            return menuList.substring(0, MAX_SIZE) + '\n... (menu truncated)';
        }
        return menuList;
    }, [config.menuItems]);

    // Build cart summary for context
    const buildCartSummary = useCallback(() => {
        const cart = config.currentCart || [];
        if (cart.length === 0) return '';

        const total = cart.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);
        const items = cart.map((item) => `${item.quantity}x ${item.name}`).join(', ');

        return `Current cart: ${items}. Total: $${total.toFixed(2)}`;
    }, [config.currentCart]);

    // Connect and start call
    const connect = useCallback(async () => {
        // User is intentionally connecting - reset the manual disconnect flag
        manuallyDisconnectedRef.current = false;
        console.log('[VapiAudio] Set manuallyDisconnected = false');

        // Reinitialize VAPI if it was destroyed by disconnect
        if (!vapiRef.current) {
            if (!config.publicKey) {
                config.onError?.('Voice service not initialized - missing public key');
                return;
            }
            console.log('[VapiAudio] VAPI instance is null, triggering reinit...');

            // Create a promise that resolves when initialization completes
            const initPromise = new Promise<void>((resolve, reject) => {
                initPromiseRef.current = { resolve, reject };
                // Timeout after 3 seconds
                setTimeout(() => {
                    if (initPromiseRef.current) {
                        initPromiseRef.current.reject(new Error('Initialization timeout'));
                        initPromiseRef.current = null;
                    }
                }, 3000);
            });

            // Reset the isInitializedRef and trigger useEffect to reinitialize
            isInitializedRef.current = false;
            setReinitTrigger(prev => prev + 1);

            // Wait for the useEffect to actually create the instance
            try {
                await initPromise;
                console.log('[VapiAudio] Reinitialization complete');
            } catch (err) {
                console.error('[VapiAudio] Failed to reinitialize VAPI instance:', err);
                config.onError?.('Voice service failed to reinitialize');
                return;
            }

            // Double-check the instance was created
            if (!vapiRef.current) {
                console.error('[VapiAudio] VAPI instance still null after init promise resolved');
                config.onError?.('Voice service initialization failed');
                return;
            }
        }

        if (status !== 'disconnected') {
            console.log('[VapiAudio] Already connected or connecting');
            return;
        }

        updateStatus('connecting');

        try {
            // Build dynamic context for the assistant
            const menuList = buildMenuList();
            const cartSummary = buildCartSummary();

            // Start the call with assistant overrides
            // NOTE: We use 'assistant-waits-for-user' because we play a pre-rendered greeting
            // from the backend for instant zero-latency feedback
            await vapiRef.current.start(config.assistantId, {
                // Don't let VAPI speak first - we play pre-rendered greeting instead
                firstMessageMode: 'assistant-waits-for-user',
                variableValues: {
                    restaurantId: config.restaurantId,
                    tableId: config.tableId,
                    tableNumber: config.tableNumber,
                    sessionId: config.customerSessionId,
                    restaurantState: config.restaurantState,
                    restaurantName: config.restaurantName || 'Restaurant',
                    menuItems: menuList,
                    cartSummary: cartSummary,
                    restaurantDescription: config.restaurantDescription || '',
                    restaurantHours: config.restaurantHours || '',
                    restaurantPhone: config.restaurantPhone || '',
                    restaurantAddress: config.restaurantAddress || '',
                },
            });

            console.log('[VapiAudio] Call started successfully');
        } catch (error: any) {
            console.error('[VapiAudio] Failed to start call:', error);
            console.error('[VapiAudio] Start error details:', {
                message: error?.message,
                response: error?.response,
                statusCode: error?.statusCode,
                assistantId: config.assistantId,
            });
            config.onError?.('Failed to start voice call');
            updateStatus('disconnected');
        }
    }, [
        config,
        status,
        updateStatus,
        buildMenuList,
        buildCartSummary,
    ]);

    // Disconnect - completely stops the VAPI call (modeled after OpenAI approach)
    const disconnect = useCallback(async () => {
        console.log('[VapiAudio] ========== DISCONNECT CALLED ==========');

        // 1. IMMEDIATELY mark as manually disconnected to block ALL events
        manuallyDisconnectedRef.current = true;

        // 2. Update state IMMEDIATELY
        updateStatus('disconnected');
        setIsSessionActive(false);

        // 3. Clear timers and tracking
        clearActivityTimer();
        processedFunctionCallsRef.current.clear();

        // 4. Stop the VAPI instance
        const vapiInstance = vapiRef.current;
        if (vapiInstance) {
            // Try to stop cleanly
            try {
                vapiInstance.stop();
            } catch (e) {
                console.log('[VapiAudio] stop() threw error (expected):', e);
            }

            // Null out immediately
            vapiRef.current = null;
            isInitializedRef.current = false;
        }

        // 5. NUCLEAR OPTION: Find and REMOVE all audio/video elements created by Daily.co/VAPI
        // This is the key difference - we actually remove the elements, not just mute them
        try {
            const audioElements = document.querySelectorAll('audio, video');
            console.log(`[VapiAudio] Found ${audioElements.length} audio/video elements`);

            audioElements.forEach((element, index) => {
                const mediaElement = element as HTMLMediaElement;

                // Mute it
                mediaElement.muted = true;

                // Pause it
                mediaElement.pause();

                // Stop all tracks if srcObject was a MediaStream
                try {
                    // Capture stream before clearing it
                    const stream = mediaElement.srcObject as MediaStream;

                    // Clear the source
                    mediaElement.src = '';
                    mediaElement.srcObject = null;

                    if (stream && stream.getTracks) {
                        stream.getTracks().forEach(track => {
                            track.stop();
                            console.log(`[VapiAudio] Stopped track: ${track.kind}`);
                        });
                    }
                } catch (e) {
                    // Ignore
                }

                // Remove it from DOM if it looks like a Daily.co element
                if (!element.id || element.id.includes('daily') || element.id === '') {
                    element.remove();
                    console.log(`[VapiAudio] Removed audio element ${index}`);
                }
            });
        } catch (e) {
            console.log('[VapiAudio] Error cleaning up audio elements:', e);
        }

        // 6. Also stop any active microphone streams
        try {
            const streams = await navigator.mediaDevices.enumerateDevices();
            console.log(`[VapiAudio] Found ${streams.length} media devices`);
        } catch (e) {
            // Can't enumerate, that's fine
        }

        console.log('[VapiAudio] ========== DISCONNECT COMPLETE ==========');
    }, [clearActivityTimer, updateStatus]);

    // Interrupt AI (stop current speech)
    const interrupt = useCallback(() => {
        console.log('[VapiAudio] Interrupting');
        if (vapiRef.current) {
            try {
                // Use mute/unmute to interrupt, as VAPI SDK doesn't have direct interrupt
                vapiRef.current.setMuted(true);
                setTimeout(() => {
                    try {
                        vapiRef.current?.setMuted(false);
                    } catch (e) {
                        console.warn('[VapiAudio] Could not unmute after interrupt:', e);
                    }
                }, 100);
            } catch (error) {
                console.warn('[VapiAudio] Could not interrupt (call may not be active):', error);
            }
        }
    }, []);

    // Send text message (for testing/fallback)
    const sendMessage = useCallback((text: string) => {
        if (vapiRef.current) {
            try {
                vapiRef.current.send({
                    type: 'add-message',
                    message: { role: 'user', content: text },
                });
            } catch (error) {
                console.warn('[VapiAudio] Could not send message:', error);
            }
        }
    }, []);

    // Toggle mute
    const toggleMute = useCallback(() => {
        if (vapiRef.current) {
            try {
                const isMuted = vapiRef.current.isMuted();
                vapiRef.current.setMuted(!isMuted);
                return !isMuted;
            } catch (error) {
                console.warn('[VapiAudio] Could not toggle mute:', error);
                return false;
            }
        }
        return false;
    }, []);

    // Start listening (for compatibility with useRealtimeAudio interface) - unmutes the microphone
    const startListening = useCallback(async () => {
        console.log('[VapiAudio] startListening called, status:', status);
        if (status === 'disconnected') {
            await connect();
        } else if (status === 'connected' || status === 'speaking') {
            // Update status first
            updateStatus('listening');

            // Try to unmute - may fail if call object not available
            if (vapiRef.current) {
                try {
                    vapiRef.current.setMuted(false);
                    console.log('[VapiAudio] Microphone unmuted');
                } catch (error) {
                    console.warn('[VapiAudio] Could not unmute (call may not be active):', error);
                }
            }
        }
    }, [status, connect, updateStatus]);

    // Stop listening (for compatibility) - mutes the microphone
    const stopListening = useCallback(() => {
        console.log('[VapiAudio] stopListening called, status:', status);

        // Update status first
        if (status === 'listening' || status === 'speaking') {
            updateStatus('connected');
        }

        // Try to mute - may fail if call object not available
        if (vapiRef.current) {
            try {
                vapiRef.current.setMuted(true);
                console.log('[VapiAudio] Microphone muted');
            } catch (error) {
                console.warn('[VapiAudio] Could not mute (call may not be active):', error);
            }
        }
    }, [status, updateStatus]);

    // Init audio (for compatibility - VAPI handles this internally)
    const initAudio = useCallback(async () => {
        // VAPI handles audio initialization internally
        // This is here for compatibility with useRealtimeAudio interface
    }, []);

    // Track conversation usage time (seconds)
    // IMPORTANT: Only bill when user is ACTIVELY interacting (listening, processing, speaking)
    // Do NOT bill for 'connected' status (pre-connection idle time)
    useEffect(() => {
        const isActivelyInteracting = status === 'listening' || status === 'processing' || status === 'speaking';

        if (isActivelyInteracting) {
            if (!usageTimerRef.current) {
                console.log('[VapiAudio] Starting usage timer (user is actively interacting)');
                lastReportTimeRef.current = Date.now();
                usageTimerRef.current = setInterval(() => {
                    const now = Date.now();
                    const deltaSeconds = Math.round((now - lastReportTimeRef.current) / 1000);

                    if (deltaSeconds >= 20) {
                        // Report usage directly to backend
                        apiRequest('POST', '/api/analytics/ai-usage/time', {
                            restaurantId: config.restaurantId,
                            seconds: deltaSeconds,
                            customerSessionId: config.customerSessionId
                        })
                            .then(() => {
                                // Only advance cursor on success
                                lastReportTimeRef.current = now;
                            })
                            .catch(err => {
                                console.warn('Error reporting Vapi usage (will retry next cycle):', err);
                            });
                    }
                }, 10000); // Check every 10s
            }
        } else if (status === 'disconnected' || status === 'connected' || status === 'connecting') {
            // Stop timer when not actively interacting (including idle 'connected' state)
            if (usageTimerRef.current) {
                // Report any remaining time before stopping
                if (lastReportTimeRef.current > 0) {
                    const deltaSeconds = Math.round((Date.now() - lastReportTimeRef.current) / 1000);
                    if (deltaSeconds > 0) {
                        console.log(`[VapiAudio] Reporting final ${deltaSeconds}s before stopping timer`);
                        apiRequest('POST', '/api/analytics/ai-usage/time', {
                            restaurantId: config.restaurantId,
                            seconds: deltaSeconds,
                            customerSessionId: config.customerSessionId
                        }).catch(err => console.warn('Error reporting final Vapi usage:', err));
                    }
                }
                console.log('[VapiAudio] Stopping usage timer (user stopped interacting)');
                clearInterval(usageTimerRef.current);
                usageTimerRef.current = null;
            }
            lastReportTimeRef.current = 0;
        }

        return () => {
            if (usageTimerRef.current) {
                clearInterval(usageTimerRef.current);
                usageTimerRef.current = null;
            }
        };
    }, [status, config.restaurantId, config.customerSessionId]);

    return {
        status,
        isSessionActive,
        connect,
        disconnect,
        interrupt,
        sendMessage,
        toggleMute,
        // Compatibility with useRealtimeAudio interface
        startListening,
        stopListening,
        initAudio,
    };
}
